(() => {
  const SELECTORS = {
    root: '[data-am-slide-destacados]',
    scroller: '[data-am-slide-destacados-scroller]',
    track: '[data-am-slide-destacados-track]',
  };

  const prefersReducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = () => window.matchMedia && window.matchMedia('(max-width: 749px)').matches;

  class AMSlideDestacados {
    constructor(root) {
      this.root = root;
      this.scroller = root.querySelector(SELECTORS.scroller);
      this.track = root.querySelector(SELECTORS.track);
      this.sectionId = root.dataset.sectionId;

      if (!this.scroller || !this.track) return;

      this.originalSlides = Array.from(this.track.children);
      this.originalCount = this.originalSlides.length;

      if (this.originalCount < 2) return;

      this.isPointerDown = false;
      this.isJumping = false;
      this.hasStabilizedInitialDesktopPosition = false;
      this.autoplayInterval = null;
      this.scrollRaf = null;
      this.normalizeTimeout = null;
      this.normalizingRaf1 = null;
      this.normalizingRaf2 = null;
      this.metaSyncRaf = null;

      this._onScroll = this.onScroll.bind(this);
      this._onPointerEnter = this.pause.bind(this);
      this._onPointerLeave = this.resume.bind(this);
      this._onFocusIn = this.pause.bind(this);
      this._onFocusOut = this.resume.bind(this);
      this._onPointerDown = () => {
        this.isPointerDown = true;
        this.pause();
      };
      this._onPointerUp = () => {
        this.isPointerDown = false;
        this.resume();
      };

      this.setupClones();
      this.measure();
      this.jumpToMiddleSet();
      this.updateActive();
      this.bind();
      this.queueInitialDesktopStabilization();
      this.scheduleMetaSync();

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => this.scheduleMetaSync());
      }

      const autoplayEnabled = root.dataset.autoplay === 'true';
      if (autoplayEnabled && !prefersReducedMotion()) this.play();
    }

    scheduleMetaSync() {
      if (this.metaSyncRaf) cancelAnimationFrame(this.metaSyncRaf);
      this.metaSyncRaf = requestAnimationFrame(() => {
        this.metaSyncRaf = null;
        this.syncMetaHeights();
      });
    }

    syncMetaHeights() {
      if (!this.track) return;

      // Medir solo slides originales (los clones copian el mismo contenido)
      const originalItems = Array.from(this.track.children).slice(this.middleStartIndex, this.middleEndExclusiveIndex);
      const metas = originalItems.map((li) => li.querySelector('.am-slide-destacados-card__meta')).filter(Boolean);

      if (!metas.length) return;

      // Reset para medir libremente
      metas.forEach((m) => {
        m.style.minHeight = '';
      });

      let maxH = 0;
      metas.forEach((m) => {
        maxH = Math.max(maxH, m.getBoundingClientRect().height);
      });

      if (maxH < 1) return;

      const value = `${Math.ceil(maxH)}px`;

      // Aplicar a todos los slides (originales + clones)
      this.track.querySelectorAll('.am-slide-destacados-card__meta').forEach((m) => {
        m.style.minHeight = value;
      });
    }

    queueInitialDesktopStabilization() {
      if (this.hasStabilizedInitialDesktopPosition) return;
      if (isMobile()) return;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.scroller || !this.track) return;
          this.measure();

          const expectedIndex = this.middleStartIndex;
          const currentIndex = this.getLeftmostSlideIndex();

          if (Number.isFinite(expectedIndex) && currentIndex !== expectedIndex) {
            this.jumpToIndex(expectedIndex);
          }

          this.hasStabilizedInitialDesktopPosition = true;
        });
      });
    }

    setupClones() {
      if (this.track.dataset.clonesReady === 'true') return;

      const cleanClone = (node) => {
        const clone = node.cloneNode(true);
        clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => {
          el.removeAttribute('data-shopify-editor-block');
        });
        clone.removeAttribute('data-shopify-editor-block');
        clone.setAttribute('data-am-slide-clone', 'true');
        return clone;
      };

      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();

      this.originalSlides.forEach((slide) => after.appendChild(cleanClone(slide)));
      this.originalSlides
        .slice()
        .reverse()
        .forEach((slide) => before.insertBefore(cleanClone(slide), before.firstChild));

      this.track.insertBefore(before, this.track.firstChild);
      this.track.appendChild(after);

      this.track.dataset.clonesReady = 'true';
    }

    measure() {
      this.slides = Array.from(this.track.children);
      if (this.slides.length < 2) return;

      const first = this.slides[0];
      const second = this.slides[1];

      this.firstOffset = first.offsetLeft;
      this.itemOffset = second.offsetLeft - first.offsetLeft;

      if (!this.itemOffset || this.itemOffset < 1) {
        this.itemOffset = first.clientWidth;
      }

      this.middleStartIndex = this.originalCount;
      this.middleEndExclusiveIndex = this.originalCount * 2;

      const middleStart = this.slides[this.middleStartIndex];
      const afterStart = this.slides[this.middleEndExclusiveIndex];
      this.setWidth =
        afterStart && middleStart
          ? afterStart.offsetLeft - middleStart.offsetLeft
          : this.itemOffset * this.originalCount;
    }

    getSlideScrollLeft(slide) {
      if (!slide) return 0;
      if (!isMobile()) return slide.offsetLeft;
      const delta = (this.scroller.clientWidth - slide.clientWidth) / 2;
      return Math.max(0, slide.offsetLeft - delta);
    }

    getClosestSlideIndex() {
      if (!this.slides?.length) return this.originalCount;

      const viewCenter = this.scroller.scrollLeft + this.scroller.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      for (let i = 0; i < this.slides.length; i += 1) {
        const slide = this.slides[i];
        const center = slide.offsetLeft + slide.clientWidth / 2;
        const dist = Math.abs(center - viewCenter);
        if (dist < closestDistance) {
          closestDistance = dist;
          closestIndex = i;
        }
      }

      return closestIndex;
    }

    getLeftmostSlideIndex() {
      if (!this.slides?.length) return this.originalCount;
      if (!this.itemOffset) return this.originalCount;

      const raw = (this.scroller.scrollLeft - this.firstOffset) / this.itemOffset;
      // En desktop queremos el slide anclado a la izquierda (estable): floor evita
      // saltos por redondeo cuando cambian widths/offsets al cargar imágenes.
      const index = Math.floor(raw + 0.00001);
      return Math.min(Math.max(index, 0), this.slides.length - 1);
    }

    updateActive() {
      if (!this.slides?.length) return;
      const index = this.getClosestSlideIndex();

      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        if (isActive) slide.setAttribute('aria-current', 'true');
        else slide.removeAttribute('aria-current');
      });
    }

    setNormalizing(isNormalizing) {
      if (!this.root) return;
      if (isNormalizing) this.root.dataset.amSlideDestacadosNormalizing = 'true';
      else delete this.root.dataset.amSlideDestacadosNormalizing;
    }

    jumpToMiddleSet() {
      this.measure();
      const middleStartIndex = this.originalCount;
      const target = this.slides[middleStartIndex];
      if (!target) return;

      this.isJumping = true;
      const prevBehavior = this.scroller.style.scrollBehavior;
      this.scroller.style.scrollBehavior = 'auto';
      this.scroller.scrollLeft = this.getSlideScrollLeft(target);

      requestAnimationFrame(() => {
        this.scroller.style.scrollBehavior = prevBehavior;
        this.isJumping = false;
        this.updateActive();
      });
    }

    getCurrentIndex() {
      return isMobile() ? this.getClosestSlideIndex() : this.getLeftmostSlideIndex();
    }

    normalizeIfNeeded() {
      if (this.isJumping || !this.slides?.length) return;

      this.measure();
      if (!this.setWidth || this.setWidth < 1) return;

      // Desktop: normaliza por umbrales de scrollLeft para evitar cambios de orden
      // causados por redondeos del índice durante el primer layout.
      if (!isMobile()) {
        const middleStart = this.slides[this.middleStartIndex];
        const middleEnd = this.slides[this.middleEndExclusiveIndex];
        if (!middleStart || !middleEnd) return;

        const startThreshold = middleStart.offsetLeft - this.itemOffset * 0.5;
        const endThreshold = middleEnd.offsetLeft - this.itemOffset * 0.5;
        const x = this.scroller.scrollLeft;

        if (x < startThreshold) this.shiftScrollBy(this.setWidth);
        else if (x >= endThreshold) this.shiftScrollBy(-this.setWidth);
        return;
      }

      // Mobile: sigue usando índice (centrado) para el loop.
      const index = this.getCurrentIndex();
      if (index < this.middleStartIndex) this.shiftScrollBy(this.setWidth);
      else if (index >= this.middleEndExclusiveIndex) this.shiftScrollBy(-this.setWidth);
    }

    shiftScrollBy(delta) {
      if (!delta) return;
      this.isJumping = true;
      this.setNormalizing(true);

      const prevBehavior = this.scroller.style.scrollBehavior;
      this.scroller.style.scrollBehavior = 'auto';
      this.scroller.scrollLeft += delta;

      requestAnimationFrame(() => {
        this.scroller.style.scrollBehavior = prevBehavior;
        this.isJumping = false;
        this.updateActive();

        if (this.normalizingRaf1) cancelAnimationFrame(this.normalizingRaf1);
        if (this.normalizingRaf2) cancelAnimationFrame(this.normalizingRaf2);

        this.normalizingRaf1 = requestAnimationFrame(() => {
          this.normalizingRaf2 = requestAnimationFrame(() => {
            this.setNormalizing(false);
            this.normalizingRaf1 = null;
            this.normalizingRaf2 = null;
          });
        });
      });
    }

    jumpToIndex(index) {
      const target = this.slides[index];
      if (!target) return;

      this.isJumping = true;
      const prevBehavior = this.scroller.style.scrollBehavior;
      this.scroller.style.scrollBehavior = 'auto';
      this.scroller.scrollLeft = this.getSlideScrollLeft(target);

      requestAnimationFrame(() => {
        this.scroller.style.scrollBehavior = prevBehavior;
        this.isJumping = false;
        this.updateActive();
      });
    }

    scrollToIndex(index) {
      const target = this.slides[index];
      if (!target) return;

      this.scroller.scrollTo({ left: this.getSlideScrollLeft(target), behavior: 'smooth' });
    }

    onScroll() {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.updateActive();
      });

      if (this.normalizeTimeout) window.clearTimeout(this.normalizeTimeout);
      this.normalizeTimeout = window.setTimeout(() => {
        this.normalizeTimeout = null;
        this.normalizeIfNeeded();
      }, 180);
    }

    play() {
      this.clearAutoplay();

      const speed = Number(this.root.dataset.speed || 4);
      const delay = Number.isFinite(speed) ? Math.max(1, speed) * 1000 : 4000;

      this.autoplayInterval = window.setInterval(() => {
        if (this.isPointerDown) return;
        if (document.hidden) return;

        this.measure();
        const current = this.getCurrentIndex();
        this.scrollToIndex(current + 1);
      }, delay);
    }

    pause() {
      this.clearAutoplay();
    }

    resume() {
      const autoplayEnabled = this.root.dataset.autoplay === 'true';
      if (!autoplayEnabled || prefersReducedMotion()) return;
      this.play();
    }

    clearAutoplay() {
      if (!this.autoplayInterval) return;
      window.clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }

    bind() {
      this.scroller.addEventListener('scroll', this._onScroll, { passive: true });

      if ('onscrollend' in this.scroller) {
        this.scroller.addEventListener('scrollend', () => {
          this.updateActive();
          this.normalizeIfNeeded();
        });
      }

      this.root.addEventListener('mouseenter', this._onPointerEnter);
      this.root.addEventListener('mouseleave', this._onPointerLeave);
      this.root.addEventListener('focusin', this._onFocusIn);
      this.root.addEventListener('focusout', this._onFocusOut);

      this.scroller.addEventListener('pointerdown', this._onPointerDown, { passive: true });
      window.addEventListener('pointerup', this._onPointerUp, { passive: true });

      this._onWindowLoad = () => this.scheduleMetaSync();
      window.addEventListener('load', this._onWindowLoad);

      this.resizeObserver = new ResizeObserver(() => {
        if (this.isJumping) return;
        this.measure();
        this.scheduleMetaSync();

        // Durante el arranque, evitamos que el ResizeObserver “congele” una
        // posición incorrecta (caso típico: viewport 1280px) antes de que la
        // estabilización inicial haya fijado el estado.
        if (!this.hasStabilizedInitialDesktopPosition && !isMobile()) return;

        let index = this.getCurrentIndex();
        if (index < this.middleStartIndex) index += this.originalCount;
        if (index >= this.middleEndExclusiveIndex) index -= this.originalCount;
        this.jumpToIndex(index);
      });
      this.resizeObserver.observe(this.scroller);
    }

    destroy() {
      this.clearAutoplay();

      if (this.normalizeTimeout) {
        window.clearTimeout(this.normalizeTimeout);
        this.normalizeTimeout = null;
      }

      if (this.normalizingRaf1) cancelAnimationFrame(this.normalizingRaf1);
      if (this.normalizingRaf2) cancelAnimationFrame(this.normalizingRaf2);
      this.normalizingRaf1 = null;
      this.normalizingRaf2 = null;
      this.setNormalizing(false);

      if (this.scroller) this.scroller.removeEventListener('scroll', this._onScroll);
      if (this.root) {
        this.root.removeEventListener('mouseenter', this._onPointerEnter);
        this.root.removeEventListener('mouseleave', this._onPointerLeave);
        this.root.removeEventListener('focusin', this._onFocusIn);
        this.root.removeEventListener('focusout', this._onFocusOut);
      }

      if (this.scroller) this.scroller.removeEventListener('pointerdown', this._onPointerDown);
      window.removeEventListener('pointerup', this._onPointerUp);
      if (this._onWindowLoad) window.removeEventListener('load', this._onWindowLoad);

      if (this.metaSyncRaf) {
        cancelAnimationFrame(this.metaSyncRaf);
        this.metaSyncRaf = null;
      }

      if (this.resizeObserver) this.resizeObserver.disconnect();
    }
  }

  const instancesBySectionId = new Map();

  const init = (root) => {
    if (!root || root.dataset.amSlideDestacadosInit === 'true') return;

    const instance = new AMSlideDestacados(root);

    root.dataset.amSlideDestacadosInit = 'true';
    if (root.dataset.sectionId) instancesBySectionId.set(root.dataset.sectionId, instance);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(SELECTORS.root).forEach(init);
  };

  document.addEventListener('DOMContentLoaded', () => initAll());

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target?.querySelector(SELECTORS.root);
    const id = section?.dataset?.sectionId;
    if (!id) return;

    const instance = instancesBySectionId.get(id);
    if (instance) instance.destroy();
    instancesBySectionId.delete(id);
  });
})();
